/**
 * Lexical tweet düğümü + tek satır sosyal URL → güvenli gömülü HTML yer tutucuları.
 * marked.parse sonrası yer tutucular iframe bloklarıyla değiştirilir.
 */

const TOKEN_PREFIX = 'COMMENT_EMBED_SLOT_'
const TOKEN_RE = /COMMENT_EMBED_SLOT_(\d+)/g

function escapeHtmlAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

export function extractYoutubeVideoId(url: string): string | null {
  const u = url.trim()
  if (!u) return null
  if (/^[a-zA-Z0-9_-]{11}$/.test(u)) return u
  const mShort = /youtu\.be\/([a-zA-Z0-9_-]{11})(?:[?&#]|$)/.exec(u)
  if (mShort) return mShort[1]
  const mEmb = /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})(?:[?&#]|$)/.exec(u)
  if (mEmb) return mEmb[1]
  const mShorts = /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})(?:[?&#]|$)/.exec(u)
  if (mShorts) return mShorts[1]
  const mWatch = /[?&]v=([a-zA-Z0-9_-]{11})(?:[&#]|$)/.exec(u)
  if (mWatch) return mWatch[1]
  return null
}

export function extractTweetStatusId(url: string): string | null {
  const u = url.trim()
  if (/^\d{8,22}$/.test(u)) return u
  const m =
    /(?:twitter\.com|x\.com)\/(?:#!\/)?(?:\w+\/)?status(?:es)?\/(\d+)/i.exec(u) ||
    /(?:twitter\.com|x\.com)\/i\/status\/(\d+)/i.exec(u)
  return m ? m[1] : null
}

export type InstagramEmbedKind = 'p' | 'reel'

export function extractInstagramShortcode(url: string): string | null {
  return extractInstagramEmbed(url)?.shortcode ?? null
}

export function extractInstagramEmbed(
  url: string,
): { shortcode: string; kind: InstagramEmbedKind } | null {
  const u = url.trim()
  const reel = /instagram\.com\/reel\/([A-Za-z0-9_-]+)/i.exec(u)
  if (reel?.[1]) return { shortcode: reel[1], kind: 'reel' }

  const post = /instagram\.com\/p\/([A-Za-z0-9_-]+)/i.exec(u)
  if (post?.[1]) return { shortcode: post[1], kind: 'p' }

  return null
}

export function buildInstagramEmbedHtml(
  shortcode: string,
  kind: InstagramEmbedKind = 'p',
): string {
  const code = shortcode.replace(/[^A-Za-z0-9_-]/g, '')
  if (!code) return ''
  const path = kind === 'reel' ? 'reel' : 'p'
  const src = `https://www.instagram.com/${path}/${escapeHtmlAttr(code)}/embed/captioned/`
  return `<div class="comment-embed comment-embed-instagram"><iframe class="comment-embed-frame" src="${src}" title="Instagram" loading="lazy" referrerpolicy="strict-origin-when-cross-origin"></iframe></div>`
}

export function buildYoutubeEmbedHtml(videoId: string): string {
  const id = videoId.replace(/[^a-zA-Z0-9_-]/g, '')
  if (id.length !== 11) return ''
  const src = `https://www.youtube-nocookie.com/embed/${escapeHtmlAttr(id)}`
  return `<div class="comment-embed comment-embed-youtube"><iframe class="comment-embed-frame" src="${src}" title="YouTube" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen="allowfullscreen" referrerpolicy="strict-origin-when-cross-origin"></iframe></div>`
}

export function buildTwitterEmbedHtml(tweetId: string): string {
  const id = tweetId.replace(/\D/g, '')
  if (!id) return ''
  const src = `https://platform.twitter.com/embed/Tweet.html?id=${escapeHtmlAttr(id)}`
  return `<div class="comment-embed comment-embed-twitter"><iframe class="comment-embed-frame" src="${src}" title="X gönderisi" loading="lazy" referrerpolicy="strict-origin-when-cross-origin"></iframe></div>`
}

/** Haber içeriği — editör gibi widgets.js ile render (iframe yok) */
export function buildArticleTweetEmbedHtml(tweetId: string): string {
  const id = tweetId.replace(/\D/g, '')
  if (!id) return ''
  return `<div class="article-tweet-embed" data-tweet-id="${escapeHtmlAttr(id)}"></div>`
}

/** Lexical export + tek satır sosyal URL → site/editör iframe HTML */
export function expandArticleEmbedsInContentHtml(html: string): string {
  if (!html?.trim()) return html

  let output = html

  output = output.replace(
    /<div\b[^>]*\bdata-lexical-tweet-id="(\d+)"[^>]*>[\s\S]*?<\/div>/gi,
    (_, rawId: string) => buildArticleTweetEmbedHtml(rawId),
  )

  output = output.replace(
    /<div class="comment-embed comment-embed-twitter"><iframe\b[^>]*Tweet\.html\?id=(\d+)[^>]*>[\s\S]*?<\/iframe><\/div>/gi,
    (_, rawId: string) => buildArticleTweetEmbedHtml(rawId),
  )

  output = output.replace(
    /<p\b[^>]*>\s*(?:<(?:span|a)[^>]*>\s*)*(https?:\/\/(?:www\.)?(?:twitter\.com|x\.com)\/[^<\s]+\/status\/(\d+)[^<\s]*)\s*(?:<\/(?:span|a)>\s*)*<\/p>/gi,
    (_full, _url: string, id: string) => buildArticleTweetEmbedHtml(id),
  )

  output = output.replace(
    /<p\b[^>]*>\s*(?:<(?:span|a)[^>]*>\s*)*(https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)[^<\s]*)\s*(?:<\/(?:span|a)>\s*)*<\/p>/gi,
    (_full, url: string) => {
      const videoId = extractYoutubeVideoId(url)
      return videoId ? buildYoutubeEmbedHtml(videoId) : _full
    },
  )

  output = output.replace(
    /<p\b[^>]*>\s*(?:<(?:span|a)[^>]*>\s*)*(https?:\/\/(?:www\.)?instagram\.com\/(?:p|reel)\/[^<\s"']+)\s*(?:<\/(?:span|a)>\s*)*<\/p>/gi,
    (_full, url: string) => {
      const instagram = extractInstagramEmbed(url)
      return instagram
        ? buildInstagramEmbedHtml(instagram.shortcode, instagram.kind)
        : _full
    },
  )

  output = output.replace(
    /<p\b[^>]*>\s*<a\b[^>]*\bhref=["'](https?:\/\/(?:www\.)?instagram\.com\/(?:p|reel)\/[^"']+)["'][^>]*>[\s\S]*?<\/a>\s*<\/p>/gi,
    (_full, url: string) => {
      const instagram = extractInstagramEmbed(url)
      return instagram
        ? buildInstagramEmbedHtml(instagram.shortcode, instagram.kind)
        : _full
    },
  )

  output = output.replace(
    /<iframe\b[^>]*\bdata-lexical-instagram=["']([^"']+)["'][^>]*(?:\bdata-lexical-instagram-kind=["']([^"']+)["'])?[^>]*(?:>[\s\S]*?<\/iframe>|\/?>)/gi,
    (_full, shortcode: string, kind?: string) => {
      const embedKind = kind === 'reel' ? 'reel' : 'p'
      return buildInstagramEmbedHtml(shortcode, embedKind)
    },
  )

  output = output.replace(
    /<div\b[^>]*\bclass=["'][^"']*comment-embed-instagram[^"']*["'][^>]*>[\s\S]*?<\/div>/gi,
    (block) => {
      const src = block.match(/\bsrc=["']([^"']+)["']/i)?.[1] ?? ''
      const instagram = extractInstagramEmbed(src)
      return instagram
        ? buildInstagramEmbedHtml(instagram.shortcode, instagram.kind)
        : block
    },
  )

  output = output.replace(
    /<blockquote\b[^>]*\bclass=["'][^"']*instagram-media[^"']*["'][^>]*>[\s\S]*?<\/blockquote>/gi,
    (block) => {
      const permalink =
        block.match(/\bdata-instgrm-permalink=["']([^"']+)["']/i)?.[1] ||
        block.match(/https?:\/\/(?:www\.)?instagram\.com\/(?:p|reel)\/[A-Za-z0-9_-]+/i)?.[0] ||
        ''
      const instagram = extractInstagramEmbed(permalink)
      return instagram
        ? buildInstagramEmbedHtml(instagram.shortcode, instagram.kind)
        : block
    },
  )

  return output
}

export type EmbedSlot = { token: string; html: string }

/**
 * Markdown içinde tweet düğümü ve tek satırlık sosyal URL’leri yer tutucuya çevirir.
 */
export function collectCommentEmbedSlots(markdown: string): {
  text: string
  slots: EmbedSlot[]
} {
  const slots: EmbedSlot[] = []
  let slotIndex = 0
  const nextToken = () => `${TOKEN_PREFIX}${slotIndex++}`

  let text = markdown

  text = text.replace(/<tweet\s+id="([^"]+)"\s*\/>/gi, (_, rawId: string) => {
    const id = String(rawId).replace(/\D/g, '')
    if (!id) return ''
    const token = nextToken()
    slots.push({ token, html: buildTwitterEmbedHtml(id) })
    return `\n\n${token}\n\n`
  })

  const lineRe =
    /^(?:\s*)(https?:\/\/\S+)(?:\s*)$/gim

  text = text.replace(lineRe, (full, url: string) => {
    const u = url.trim()
    const yt = extractYoutubeVideoId(u)
    if (yt) {
      const token = nextToken()
      slots.push({ token, html: buildYoutubeEmbedHtml(yt) })
      return `\n\n${token}\n\n`
    }
    const tw = extractTweetStatusId(u)
    if (tw) {
      const token = nextToken()
      slots.push({ token, html: buildTwitterEmbedHtml(tw) })
      return `\n\n${token}\n\n`
    }
    const ig = extractInstagramEmbed(u)
    if (ig) {
      const token = nextToken()
      slots.push({ token, html: buildInstagramEmbedHtml(ig.shortcode, ig.kind) })
      return `\n\n${token}\n\n`
    }
    return full
  })

  return { text, slots }
}

/** marked çıktısındaki yer tutucuları (p sarmalı dahil) iframe ile değiştirir */
export function applyCommentEmbedSlots(
  html: string,
  slots: EmbedSlot[],
): string {
  let out = html
  for (const { token, html: embedHtml } of slots) {
    const esc = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    out = out.replace(new RegExp(`<p>\\s*${esc}\\s*</p>`, 'gi'), embedHtml)
    out = out.replace(new RegExp(esc, 'g'), embedHtml)
  }
  return out
}

export { TOKEN_RE }
